import React, { useCallback, useMemo } from 'react'
import { useAuth } from 'src/contexts/auth'
import { AnimalController } from 'src/controllers/animal.controller'
import { SelectFetchInput } from '../SelectFetchInput'
import { useAnimalSelect } from 'src/pages/Protected/Animal/components/views/GeneralInformations/hooks/useAnimalSelect'
import { useStyles } from './styles'
import { GenderFemaleIcon, GenderMaleIcon } from 'src/assets/svg'
import { IAnimal } from 'src/dto/animal.dto'

interface SelectAnimalFetchInputProps {
  label: string
  value: string
  id?: string
  placeholder?: string
  error?: string
  selectBy: any
  showBy: any
  className?: any
  name?: string
  disabled?: boolean
  onChange: (item: any) => void
  filteredSex?: 'Fêmea' | 'Macho'
  notShowAnimals?: IAnimal[]
  hidden?: ('VD' | 'MT')[]
  filter?: (filteredAnimalsList: IAnimal[]) => IAnimal[]
}

export const SelectAnimalFetchInput = ({
  label,
  value,
  id,
  placeholder = 'Selecionar',
  error,
  selectBy,
  className,
  disabled = false,
  showBy,
  onChange,
  filteredSex,
  notShowAnimals,
  hidden = [],
  filter,
  name,
  ...rest
}: SelectAnimalFetchInputProps) => {
  const { userFarm: farmId } = useAuth()

  const { animalList, hasNextPage, isFiltering, onFetchMore, onSearchChange } = useAnimalSelect({
    currentAnimalId: value,
    sexFilter: filteredSex && filteredSex,
    selectId: id
  })

  const list = useMemo(() => {
    const filteredAnimals =
      hidden && hidden.length > 0
        ? animalList.filter((animal) => !hidden.includes(animal?.categoria as 'VD' | 'MT'))
        : animalList

    if (!notShowAnimals || notShowAnimals.length === 0) {
      return filteredAnimals.map((animal) => ({
        ...animal,
        nome: !animal.nome || animal.nome.trim() === '' ? 'Não informado' : animal.nome
      }))
    }

    const filteredAnimalsList = filteredAnimals.filter(
      (animal) => !notShowAnimals.some((selected) => selected.idAnimal === animal.idAnimal)
    )
    return filter ? filter(filteredAnimalsList) : filteredAnimalsList
  }, [animalList, notShowAnimals, hidden, filter])

  const getAnimalById = useCallback(
    async (id: string) => {
      const animalController = new AnimalController()
      try {
        return await animalController.getAnimalById(farmId!, id)
      } catch (error) {
        console.error('Erro ao buscar animal pelo ID', error)
      }
    },
    [farmId]
  )

  const classes = useStyles()

  const renderCustomOption = (option: any) => {
    return (
      <div className={classes.option}>
        <div className={classes.animalInfo}>
          <span className={classes.animalName}>{option.nome || 'Não informado'}</span>
          <span className={classes.animalNumber}>Número: {option.cdn}</span>
        </div>
        <span className={`${classes.sexIcon}`}>{option.sexo === 'F' ? <GenderFemaleIcon /> : <GenderMaleIcon />}</span>
      </div>
    )
  }

  return (
    <div className={className} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div style={{ flex: 1 }}>
        <SelectFetchInput
          id={id}
          className={className}
          label={label}
          selectBy={selectBy}
          showBy={showBy}
          options={list}
          value={value}
          error={error}
          placeholder={placeholder}
          onChange={onChange}
          fetchProps={{
            onFetchMore,
            hasNextPage,
            isFetching: isFiltering,
            onSearchChange,
            fetchById: getAnimalById
          }}
          disabled={disabled}
          renderOption={renderCustomOption}
          {...rest}
        />
      </div>
    </div>
  )
}
